/**
 * The inline marker for a clinical inference, the sibling of the citation dagger.
 *
 * A generated clinical document is not uniformly checkable. An extractable fact
 * either appears in the record or does not, and gets a `†` citation resolving to
 * the exact source line. A clinical *inference* — "anxiety drives behavioural
 * rigidity" — is derived ACROSS the record and by construction appears nowhere in
 * it verbatim. Giving it a source citation would assert proof that does not
 * exist; giving it nothing would present interpretation as plain fact. It gets
 * its own marker instead.
 *
 * `‡` is deliberately the typographic sibling of `†`: the captain's decision was
 * two dagger *variants*, not two different shapes, so the two read as one
 * vocabulary while staying distinguishable. The colour differs for the same
 * reason — distinct from the citation blue, but not a warning colour, because an
 * inference is the product working rather than a defect.
 *
 * The popover names the Blueprint cards the inference was drawn from and reveals
 * one on click. Raw `ENT-` ids are never shown (the clinician-simple rule).
 */
import React, { useId, useState } from "react";
import { createPortal } from "react-dom";
import { useRevealCard } from "../RevealCardContext";

/** Viewport-relative coordinates for positioning a portaled popover. */
interface PopoverPosition {
  top: number;
  left: number;
}

/**
 * Compute where to render the portaled popover, anchored under its trigger.
 *
 * @param anchor - The marker element the popover hangs off of.
 * @returns The `top`/`left` viewport coordinates.
 */
function computePopoverPosition(anchor: HTMLElement): PopoverPosition {
  const rect = anchor.getBoundingClientRect();
  return { top: rect.bottom + 4, left: rect.left + rect.width / 2 };
}

/**
 * The click-popover: what this inference was built from.
 *
 * Rendered through a `document.body` portal and fixed-positioned off the
 * trigger's live bounding rect, so it floats above any ancestor's
 * `overflow: hidden` instead of being clipped by it.
 *
 * @param props - The contributing cards, the trigger element, and a close handler.
 * @returns The portaled popover element.
 */
function InferencePopover({
  cards,
  anchor,
  onClose,
}: {
  cards: { id: string; name: string }[];
  anchor: HTMLElement;
  onClose: () => void;
}): React.ReactElement {
  const revealCard = useRevealCard();
  const { top, left } = computePopoverPosition(anchor);
  return createPortal(
    <span
      role="dialog"
      data-testid="inference-popover"
      style={{ top, left }}
      className="fixed z-50 w-72 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 text-left text-xs font-normal text-slate-700 shadow-xl"
    >
      <span className="mb-1 block font-semibold text-slate-500">
        Clinical interpretation
      </span>
      <span className="mb-2 block text-slate-600">
        Drawn from the record rather than quoted from it.
      </span>
      {cards.length > 0 ? (
        <span className="block">
          <span className="mb-1 block font-medium text-slate-500">
            Built from
          </span>
          {cards.map((card) => (
            <button
              key={card.id}
              type="button"
              data-testid="inference-card"
              onClick={() => {
                onClose();
                revealCard(card.id);
              }}
              className="block w-full rounded px-1 py-0.5 text-left text-violet-700 hover:bg-violet-50 hover:underline"
            >
              {card.name}
            </button>
          ))}
        </span>
      ) : (
        <span className="block italic text-slate-500">
          No single card carries this — it draws on the record as a whole.
        </span>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="mt-2 block text-[0.7rem] text-slate-400 hover:text-slate-600"
      >
        Close
      </button>
    </span>,
    document.body,
  );
}

/**
 * Render one inference marker with its hover label and click popover.
 *
 * @param props - The contributing cards for this inference.
 * @returns The inline marker element.
 */
export function InferenceMarker({
  cards,
}: {
  cards: { id: string; name: string }[];
}): React.ReactElement {
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState(false);
  const [markerEl, setMarkerEl] = useState<HTMLElement | null>(null);
  const tipId = useId();
  const label = "Clinical interpretation — drawn from the record, not quoted";
  return (
    <span className="relative inline-block align-baseline">
      <sup
        ref={setMarkerEl}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={shown && !open ? tipId : undefined}
        title={label}
        data-testid="inference-marker"
        onMouseEnter={() => setShown(true)}
        onMouseLeave={() => setShown(false)}
        onFocus={() => setShown(true)}
        onBlur={() => setShown(false)}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        className="ml-0.5 cursor-pointer rounded text-[0.65em] font-medium text-violet-500 hover:text-violet-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-violet-400"
      >
        ‡
      </sup>
      {shown && !open && (
        <span
          id={tipId}
          role="tooltip"
          data-testid="inference-tooltip"
          className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[0.7rem] font-normal text-white shadow-lg"
        >
          {label}
        </span>
      )}
      {open && markerEl && (
        <InferencePopover
          cards={cards}
          anchor={markerEl}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}
